import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { LayoutGrid, Search, CheckCircle2, Loader2, Moon, Factory, ArrowLeftRight } from 'lucide-react';
import { toast } from 'sonner';
import { STATUS_META, LINES } from '../mock/data';
import { PageHeader, StatCard } from '../components/common';
import GettingStarted from '../components/GettingStarted';
import { useAuth } from '../context/AuthContext';
import { useWarehouse } from '../context/WarehouseContext';
import { useI18n } from '../i18n/I18nContext';
import { useRealtime } from '../realtime/RealtimeContext';
import api from '../api/client';

// Parse "HH:MM-HH:MM" from a shift string and check if current local time is within it
function isWithinShift(shiftStr) {
  if (!shiftStr) return true;
  const m = shiftStr.match(/(\d{2}):(\d{2})\s*-\s*(\d{2}):(\d{2})/);
  if (!m) return true;
  const now = new Date();
  const cur = now.getHours() * 60 + now.getMinutes();
  const start = parseInt(m[1]) * 60 + parseInt(m[2]);
  const end = parseInt(m[3]) * 60 + parseInt(m[4]);
  if (end > start) return cur >= start && cur < end;
  return cur >= start || cur < end; // overnight wrap
}

function MachineCard({ m, onAction, t, offShift }) {
  const meta = STATUS_META[m.status] || STATUS_META.IDLE;
  return (
    <div className="kmt-panel p-4 flex flex-col gap-3 transition-colors"
      style={offShift ? { borderColor: 'var(--kmt-amber)', boxShadow: '0 0 0 1px rgba(240,169,43,0.25)' } : undefined}>
      <div className="flex items-start justify-between">
        <div>
          <div className="font-mono-k font-semibold text-sm" style={{ color: 'var(--kmt-gold)' }}>{m.id}</div>
          <div className="font-display font-medium text-[15px] mt-0.5">{m.displayName}</div>
          <div className="text-xs mt-0.5" style={{ color: 'var(--kmt-muted)' }}>{m.line} · {m.type}</div>
        </div>
        <span className={`kmt-badge ${meta.cls}`}><span className={`kmt-dot ${meta.dot}`} />{t(`status.${m.status.toLowerCase()}`)}</span>
      </div>
      <div className="flex items-center justify-between text-xs" style={{ color: 'var(--kmt-muted)' }}>
        <span className="truncate flex items-center gap-1.5">
          {offShift && <Moon size={12} style={{ color: 'var(--kmt-amber)' }} />}
          {m.operator}
        </span>
        <span className="font-mono-k">{m.runtime}</span>
      </div>
      {offShift && (
        <div className="kmt-badge s-problem" style={{ fontSize: 9, alignSelf: 'flex-start' }}>Operator off-shift</div>
      )}
      <div className="flex gap-2">
        <button className="kmt-mini start" onClick={() => onAction('START', m)}>{t('common.start')}</button>
        <button className="kmt-mini stop" onClick={() => onAction('STOP', m)}>{t('common.stop')}</button>
        <button className="kmt-mini brk" onClick={() => onAction('BREAK', m)}>{t('common.break')}</button>
      </div>
      <button className="text-xs flex items-center gap-1.5 justify-center pt-1" style={{ color: 'var(--kmt-muted)' }}
        onClick={() => onAction('PROBLEM', m)}>
        <span style={{ color: 'var(--kmt-amber)' }}>⚠</span> {t('common.reportProblem')}
      </button>
    </div>
  );
}

export default function LiveMachines() {
  const { user } = useAuth();
  const wh = useWarehouse();
  const { t } = useI18n();
  const [machines, setMachines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [lineFilter, setLineFilter] = useState('ALL');
  const [assignments, setAssignments] = useState([]);

  const load = useCallback(async () => {
    try {
      const data = await api.listMachines();
      setMachines(data);
    } catch (e) {
      toast.error('Failed to load machines');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    api.listAssignments().then(setAssignments).catch(() => {});
    const timer = setInterval(load, 30000); // auto-refresh every 30s
    return () => clearInterval(timer);
  }, [load]);

  useRealtime(() => { load(); api.listAssignments().then(setAssignments).catch(() => {}); });

  // operator -> shift string map, then compute off-shift set for machines
  const offShiftMap = useMemo(() => {
    const opShift = {};
    assignments.forEach((a) => { opShift[a.operator] = a.shift; });
    const map = {};
    machines.forEach((m) => {
      const op = m.operator;
      if (op && op !== 'Unassigned' && opShift[op]) {
        map[m.id] = !isWithinShift(opShift[op]);
      }
    });
    return map;
  }, [assignments, machines]);

  const offShiftCount = useMemo(() => Object.values(offShiftMap).filter(Boolean).length, [offShiftMap]);

  const counts = useMemo(() => {
    const c = { RUNNING: 0, STOPPED: 0, PROBLEM: 0, SETTING: 0, BREAK: 0, IDLE: 0 };
    machines.forEach((m) => { c[m.status] = (c[m.status] || 0) + 1; });
    return c;
  }, [machines]);

  const filtered = useMemo(() => machines.filter((m) => {
    const q = query.toLowerCase();
    const matchQ = !q || m.id.toLowerCase().includes(q) || m.displayName.toLowerCase().includes(q) || m.type.toLowerCase().includes(q);
    const matchS = statusFilter === 'ALL' || m.status === statusFilter;
    const matchL = lineFilter === 'ALL' || m.line === lineFilter;
    return matchQ && matchS && matchL;
  }), [machines, query, statusFilter, lineFilter]);

  const onAction = async (action, m) => {
    // optimistic update
    const nextStatus = { START: 'RUNNING', STOP: 'STOPPED', BREAK: 'BREAK', PROBLEM: 'PROBLEM' }[action];
    setMachines((prev) => prev.map((x) => (x.id === m.id ? { ...x, status: nextStatus } : x)));
    try {
      const updated = await api.machineAction(m.id, action, user?.name);
      setMachines((prev) => prev.map((x) => (x.id === m.id ? updated : x)));
      toast.success(`${m.id} — ${t(`status.${nextStatus.toLowerCase()}`)}`, { description: m.displayName });
    } catch (e) {
      toast.error('Action failed');
      load();
    }
  };

  const stats = [
    { label: t('status.running'), value: counts.RUNNING, accent: 'var(--kmt-green)' },
    { label: t('status.stopped'), value: counts.STOPPED, accent: 'var(--kmt-red)' },
    { label: t('status.problem'), value: counts.PROBLEM, accent: 'var(--kmt-amber)' },
    { label: t('status.setting'), value: counts.SETTING, accent: '#a855f7' },
    { label: t('status.break'), value: counts.BREAK, accent: 'var(--kmt-blue)' },
    { label: t('status.idle'), value: counts.IDLE, accent: 'var(--kmt-idle)' },
  ];

  return (
    <div className="kmt-fade-in">
      <PageHeader icon={LayoutGrid} title={t('live.title')} subtitle={`${machines.length} ${t('live.subtitle')}`}
        right={<div className="flex items-center gap-3">
          {offShiftCount > 0 && (
            <div className="flex items-center gap-2 kmt-panel-2 px-3.5 py-2 rounded-lg" style={{ borderColor: 'rgba(240,169,43,0.4)' }}>
              <Moon size={15} style={{ color: 'var(--kmt-amber)' }} />
              <span className="text-sm">{offShiftCount} off-shift</span>
            </div>
          )}
          <div className="flex items-center gap-2 kmt-panel-2 px-3.5 py-2 rounded-lg">
            <CheckCircle2 size={15} style={{ color: 'var(--kmt-green)' }} />
            <span className="text-sm">{t('common.welcome')}, {user?.name}</span>
          </div>
        </div>} />

      <div className="p-8 space-y-6">
        <GettingStarted />
        {wh?.canSwitch && <FactoryOverview wh={wh} />}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
          {stats.map((s) => <StatCard key={s.label} {...s} />)}
        </div>

        <div className="flex flex-col md:flex-row gap-3">
          <div className="relative flex-1">
            <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2" style={{ color: 'var(--kmt-muted-2)' }} />
            <input className="kmt-input" style={{ paddingLeft: 40 }} placeholder={t('live.searchPlaceholder')}
              value={query} onChange={(e) => setQuery(e.target.value)} />
          </div>
          <select className="kmt-input md:w-44" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="ALL">{t('common.allStatuses')}</option>
            {Object.keys(STATUS_META).map((s) => <option key={s} value={s}>{t(`status.${s.toLowerCase()}`)}</option>)}
          </select>
          <select className="kmt-input md:w-40" value={lineFilter} onChange={(e) => setLineFilter(e.target.value)}>
            <option value="ALL">{t('common.allLines')}</option>
            {LINES.map((l) => <option key={l} value={l}>{l}</option>)}
          </select>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-24" style={{ color: 'var(--kmt-muted)' }}>
            <Loader2 className="animate-spin" size={28} />
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-4">
              {filtered.map((m) => <MachineCard key={m.id} m={m} onAction={onAction} t={t} offShift={!!offShiftMap[m.id]} />)}
            </div>
            {filtered.length === 0 && (
              <div className="text-center py-16" style={{ color: 'var(--kmt-muted)' }}>No machines match your filters.</div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function FactoryOverview({ wh }) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = useCallback(() => api.warehousesOverview().then(setData).catch(() => setData([])).finally(() => setLoading(false)), []);
  useEffect(() => { load(); }, [load]);
  useRealtime(() => load());

  if (loading || data.length === 0) return null;

  return (
    <div className="kmt-panel p-5" data-testid="factory-overview">
      <div className="flex items-center gap-2 mb-4">
        <Factory size={17} style={{ color: 'var(--kmt-gold)' }} />
        <div className="font-display font-semibold">Factory Overview</div>
        <span className="text-xs" style={{ color: 'var(--kmt-muted)' }}>· {data.length} {data.length === 1 ? 'factory' : 'factories'}</span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {data.map((f) => {
          const active = f.id === wh?.activeId;
          return (
            <div key={f.id} data-testid={`factory-card-${f.id}`}
              className="rounded-lg p-4 transition-colors"
              style={{ background: 'var(--kmt-panel-2)', border: active ? '1px solid var(--kmt-gold)' : '1px solid var(--kmt-border)' }}>
              <div className="flex items-start justify-between mb-3">
                <div>
                  <div className="font-display font-medium flex items-center gap-2">
                    {f.name}
                    {active && <span className="kmt-badge s-completed" style={{ fontSize: 9 }}>Active</span>}
                  </div>
                  {f.location && <div className="text-xs mt-0.5" style={{ color: 'var(--kmt-muted)' }}>{f.location}</div>}
                </div>
                {!active && (
                  <button className="text-xs flex items-center gap-1 kmt-btn-ghost" style={{ padding: '4px 8px' }}
                    onClick={() => wh.setActive(f.id)} data-testid={`factory-switch-${f.id}`}>
                    <ArrowLeftRight size={12} />Switch
                  </button>
                )}
              </div>
              <div className="grid grid-cols-4 gap-2 text-center">
                <div><div className="font-display text-lg font-semibold">{f.machines}</div><div className="text-xs" style={{ color: 'var(--kmt-muted)' }}>Machines</div></div>
                <div><div className="font-display text-lg font-semibold" style={{ color: 'var(--kmt-green)' }}>{f.running}</div><div className="text-xs" style={{ color: 'var(--kmt-muted)' }}>Running</div></div>
                <div><div className="font-display text-lg font-semibold" style={{ color: f.openProblems ? 'var(--kmt-amber)' : 'inherit' }}>{f.openProblems}</div><div className="text-xs" style={{ color: 'var(--kmt-muted)' }}>Problems</div></div>
                <div><div className="font-display text-lg font-semibold" style={{ color: 'var(--kmt-blue)' }}>{f.activeJobs}</div><div className="text-xs" style={{ color: 'var(--kmt-muted)' }}>Jobs</div></div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
